import { useTranslate } from "@tolgee/react";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { appConfig } from "../config";

/**
 * Hook that intercepts all browser network requests and shows a toast for long-running requests
 */
export const useGlobalNetworkToast = () => {
  const pendingRequestsRef = useRef<number>(0);
  const toastIdRef = useRef<string | number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslate();
  useEffect(() => {
    // Store original fetch and XMLHttpRequest methods
    const originalFetch = window.fetch;
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    // Helper to handle request start
    const handleRequestStart = () => {
      pendingRequestsRef.current++;

      // If this is the first pending request, start a timer
      if (pendingRequestsRef.current === 1 && !timerRef.current) {
        timerRef.current = setTimeout(() => {
          // After delay, if still pending, show toast
          if (pendingRequestsRef.current > 0 && !toastIdRef.current) {
            toastIdRef.current = toast.loading(t("loading-data-toast"), {
              autoClose: false,
              closeButton: false,
            });
          }
        }, appConfig.networkToastDelay);
      }
    };

    // Helper to handle request end
    const handleRequestEnd = () => {
      pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1);

      // If no more pending requests, clear timer and dismiss toast
      if (pendingRequestsRef.current === 0) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
      }
    };

    // Override fetch
    window.fetch = function (...args) {
      handleRequestStart();

      return originalFetch
        .apply(this, args)
        .then((response) => {
          handleRequestEnd();
          return response;
        })
        .catch((error) => {
          handleRequestEnd();
          throw error;
        });
    };

    // Override XMLHttpRequest
    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async: boolean = true,
      username?: string | null,
      password?: string | null
    ) {
      return originalXhrOpen.call(
        this,
        method,
        url,
        async,
        username || null,
        password || null
      );
    };

    XMLHttpRequest.prototype.send = function (...args) {
      handleRequestStart();

      // Add event listeners for completion
      this.addEventListener("load", handleRequestEnd);
      this.addEventListener("error", handleRequestEnd);
      this.addEventListener("abort", handleRequestEnd);

      return originalXhrSend.apply(this, args);
    };

    // Cleanup function to restore original methods
    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXhrOpen;
      XMLHttpRequest.prototype.send = originalXhrSend;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [t]);
};
